/**
 * withSkreen - Main Application
 * Visa Interview Preparation with Local LLM Analysis
 */

import { createClient, type Client } from '@browser-first-ai/sdk';
import {
  analyzeLinkedInData,
  createEmptyLinkedInData,
  parsePositions,
  parseEducation,
  parseConnections,
  parseJobApplications,
  parseProfile,
  parseMessages,
  parsePublications,
  parseCauses,
  parseProjects,
  parseRecommendations,
  parseShares,
  parseComments,
  parseArticle,
  type LinkedInData,
  type VisaAnalysisResult,
  type RedFlag,
  type TimelineEntry,
} from './index.js';
import {
  chunkContent,
  chunkTwitterContent,
  preFilterChunks,
  analyzeContentWithLLM,
  type LLMAnalysisResult,
  type LLMFlag,
  type ContentChunk,
} from './analyzers/llmAnalyzer.js';
import {
  isTwitterExport,
  parseTwitterData,
  type TwitterData,
} from './parsers/twitter/index.js';
import {
  parseVisaDocument,
  checkWebGPUSupport,
  detectDocumentType,
  type ParsedVisaDocument,
  type DS160Data,
  type I129Data,
} from './parsers/pdf/index.js';
import { crossValidate } from './analyzers/crossValidator.js';
import {
  getSeverityLabel,
  type CrossValidationResult,
  type Discrepancy,
} from './analyzers/crossValidationTypes.js';
import { unzipSync, Unzip, UnzipInflate } from 'fflate';

// Marked for markdown rendering
declare const marked: { parse: (md: string) => string };

// PostHog analytics
declare const posthog: {
  capture: (event: string, properties?: Record<string, unknown>) => void;
} | undefined;

function track(event: string, properties?: Record<string, unknown>): void {
  try {
    if (typeof posthog !== 'undefined' && posthog?.capture) {
      posthog.capture(event, properties);
    }
  } catch (e) {
    // Analytics should never break the app
    console.debug('Analytics error:', e);
  }
}

// Selectively unzip only needed files (for large archives like Twitter)
function selectiveUnzip(data: Uint8Array, neededFiles: string[]): Promise<Record<string, Uint8Array>> {
  return new Promise((resolve, reject) => {
    const result: Record<string, Uint8Array> = {};
    const neededSet = new Set(neededFiles);
    let pending = neededFiles.length;

    const uz = new Unzip((file) => {
      // Only process files we need
      if (neededSet.has(file.name)) {
        const chunks: Uint8Array[] = [];
        file.ondata = (err, chunk, final) => {
          if (err) {
            reject(err);
            return;
          }
          chunks.push(chunk);
          if (final) {
            // Combine chunks
            const totalLen = chunks.reduce((acc, c) => acc + c.length, 0);
            const combined = new Uint8Array(totalLen);
            let offset = 0;
            for (const c of chunks) {
              combined.set(c, offset);
              offset += c.length;
            }
            result[file.name] = combined;
            pending--;
            if (pending === 0) {
              resolve(result);
            }
          }
        };
        file.start();
      }
    });

    uz.register(UnzipInflate);

    // Process in chunks to avoid blocking
    const CHUNK_SIZE = 65536;
    let offset = 0;

    function processChunk() {
      const end = Math.min(offset + CHUNK_SIZE, data.length);
      const chunk = data.subarray(offset, end);
      const isFinal = end >= data.length;

      uz.push(chunk, isFinal);
      offset = end;

      if (!isFinal) {
        setTimeout(processChunk, 0);
      } else if (pending === 0) {
        resolve(result);
      }
    }

    processChunk();
  });
}

// === State ===
let client: Client | null = null;
let analysisResult: VisaAnalysisResult | null = null;
let linkedInData: LinkedInData | null = null;
let llmAnalysisResult: LLMAnalysisResult | null = null;

// Visa document state
let ds160Data: DS160Data | null = null;
let i129Data: I129Data | null = null;
let crossValidationResult: CrossValidationResult | null = null;

// Track uploaded platforms
const uploadedPlatforms = {
  linkedin: false,
  twitter: false,
  ds160: false,
  i129: false,
};

// === DOM Elements ===
const uploadArea = document.getElementById('upload-area') as HTMLDivElement;
const fileInput = document.getElementById('file-input') as HTMLInputElement;
const linkedinCard = document.getElementById('linkedin-card') as HTMLDivElement;
const twitterCard = document.getElementById('twitter-card') as HTMLDivElement;
const linkedinInput = document.getElementById('linkedin-input') as HTMLInputElement;
const twitterInput = document.getElementById('twitter-input') as HTMLInputElement;
const linkedinStatus = document.getElementById('linkedin-status') as HTMLParagraphElement;
const twitterStatus = document.getElementById('twitter-status') as HTMLParagraphElement;
const progressSection = document.getElementById('progress-section') as HTMLDivElement;
const progressFill = document.getElementById('progress-fill') as HTMLDivElement;
const progressText = document.getElementById('progress-text') as HTMLDivElement;
const resultsSection = document.getElementById('results') as HTMLDivElement;
const riskBadge = document.getElementById('risk-badge') as HTMLDivElement;
const statCritical = document.getElementById('stat-critical') as HTMLDivElement;
const statWarnings = document.getElementById('stat-warnings') as HTMLDivElement;
const statPositive = document.getElementById('stat-positive') as HTMLDivElement;
const statYears = document.getElementById('stat-years') as HTMLDivElement;
const flagsList = document.getElementById('flags-list') as HTMLDivElement;
const timelineList = document.getElementById('timeline-list') as HTMLDivElement;
const llmStatus = document.getElementById('llm-status') as HTMLDivElement;
const llmInput = document.getElementById('llm-input') as HTMLInputElement;
const llmSend = document.getElementById('llm-send') as HTMLButtonElement;
const llmOutput = document.getElementById('llm-output') as HTMLDivElement;
const quickQuestions = document.getElementById('quick-questions') as HTMLDivElement;

// Visa Documents DOM elements
const ds160Card = document.getElementById('ds160-card') as HTMLDivElement;
const i129Card = document.getElementById('i129-card') as HTMLDivElement;
const ds160Input = document.getElementById('ds160-input') as HTMLInputElement;
const i129Input = document.getElementById('i129-input') as HTMLInputElement;
const ds160Status = document.getElementById('ds160-status') as HTMLParagraphElement;
const i129Status = document.getElementById('i129-status') as HTMLParagraphElement;
const ocrProgress = document.getElementById('ocr-progress') as HTMLDivElement;
const ocrProgressText = document.getElementById('ocr-progress-text') as HTMLParagraphElement;

// Cross-Validation DOM elements
const crossValidationSection = document.getElementById('cross-validation-section') as HTMLElement;
const crossValidationRecommendations = document.getElementById('cross-validation-recommendations') as HTMLDivElement;
const discrepancyList = document.getElementById('discrepancy-list') as HTMLDivElement;
const cvCritical = document.getElementById('cv-critical') as HTMLSpanElement;
const cvWarnings = document.getElementById('cv-warnings') as HTMLSpanElement;
const cvInfo = document.getElementById('cv-info') as HTMLSpanElement;
const cvCoverage = document.getElementById('cv-coverage') as HTMLSpanElement;

// === Progress Helpers ===
function showProgress(show: boolean): void {
  progressSection.classList.toggle('visible', show);
}

// Default status messages for each platform
const DEFAULT_STATUS = {
  linkedin: '.zip file',
  twitter: '.zip file',
};

// Update platform card status
function updatePlatformCard(platform: 'linkedin' | 'twitter', status: 'processing' | 'uploaded' | 'error', message?: string): void {
  const card = platform === 'linkedin' ? linkedinCard : twitterCard;
  const statusEl = platform === 'linkedin' ? linkedinStatus : twitterStatus;

  card.classList.remove('processing', 'uploaded');

  if (status === 'processing') {
    card.classList.add('processing');
    statusEl.textContent = message || 'Analyzing...';
  } else if (status === 'uploaded') {
    card.classList.add('uploaded');
    statusEl.textContent = message || 'Done';
    uploadedPlatforms[platform] = true;
  } else if (status === 'error') {
    statusEl.textContent = message || DEFAULT_STATUS[platform];
  }
}

function setProgress(percent: number, text: string): void {
  progressFill.style.width = `${percent}%`;
  progressText.textContent = text;
}

// Update visa document card status
function updateVisaDocCard(
  docType: 'ds160' | 'i129',
  status: 'processing' | 'uploaded' | 'error',
  message?: string
): void {
  const card = docType === 'ds160' ? ds160Card : i129Card;
  const statusEl = docType === 'ds160' ? ds160Status : i129Status;

  card.classList.remove('processing', 'uploaded');

  if (status === 'processing') {
    card.classList.add('processing');
    statusEl.textContent = message || 'Processing...';
  } else if (status === 'uploaded') {
    card.classList.add('uploaded');
    statusEl.textContent = message || 'Analyzed';
    uploadedPlatforms[docType] = true;
  } else if (status === 'error') {
    statusEl.textContent = message || '.pdf file';
  }
}

// Show/hide OCR progress
function showOCRProgress(show: boolean, text?: string): void {
  if (show) {
    ocrProgress.classList.remove('hidden');
    if (text) ocrProgressText.textContent = text;
  } else {
    ocrProgress.classList.add('hidden');
  }
}

// Helper to decode Uint8Array to string
const textDecoder = new TextDecoder('utf-8');
function decodeFile(data: Uint8Array): string {
  return textDecoder.decode(data);
}

// Twitter data files we need (skip media which is 99% of the archive)
const TWITTER_DATA_FILES = [
  'data/tweets.js',
  'data/direct-messages.js',
  'data/profile.js',
  'data/account.js',
  'data/like.js',
  'data/note-tweet.js',
];

// === File Processing ===
async function processZipFile(file: File, platformHint?: 'linkedin' | 'twitter'): Promise<void> {
  const startTime = performance.now();
  const fileSizeMB = file.size / 1024 / 1024;

  console.log('%c========================================', 'color: blue; font-weight: bold');
  console.log('%c PROCESSING NEW FILE: ' + file.name, 'color: blue; font-weight: bold');
  console.log('%c File size: ' + fileSizeMB.toFixed(2) + ' MB', 'color: blue');
  console.log('%c========================================', 'color: blue; font-weight: bold');

  // Detect platform
  const isLikelyTwitter = platformHint === 'twitter' || file.name.toLowerCase().includes('twitter');
  const detectedPlatform: 'linkedin' | 'twitter' = isLikelyTwitter ? 'twitter' : 'linkedin';

  // Track upload started
  track('upload_started', {
    platform: detectedPlatform,
    file_size_mb: Math.round(fileSizeMB * 100) / 100,
  });

  // Update card status
  updatePlatformCard(detectedPlatform, 'processing');
  showProgress(true);
  setProgress(5, 'Reading zip file...');

  try {
    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    const zipData = new Uint8Array(arrayBuffer);

    let actualPlatform: 'linkedin' | 'twitter' = detectedPlatform;

    if (isLikelyTwitter) {
      // Twitter exports are huge due to media - only extract data files we need
      console.log('%c=== DETECTED: TWITTER EXPORT (selective extraction) ===', 'color: #1DA1F2; font-weight: bold');
      setProgress(15, 'Extracting Twitter data files...');

      const unzipped = await selectiveUnzip(zipData, TWITTER_DATA_FILES);
      const extractedFiles = Object.keys(unzipped);
      console.log(`%cExtracted ${extractedFiles.length} data files:`, 'color: #1DA1F2', extractedFiles);

      await processTwitterExport(unzipped);
      actualPlatform = 'twitter';
    } else {
      // LinkedIn exports are smaller - extract everything
      setProgress(10, 'Extracting archive...');
      const unzipped = unzipSync(zipData);
      const fileList = Object.keys(unzipped).filter(f => !f.endsWith('/'));

      console.log('%c=== FILES IN ZIP ===', 'color: green; font-weight: bold');
      console.log(`%cTotal files: ${fileList.length}`, 'color: green');

      // Double-check if it's actually Twitter (in case filename didn't indicate)
      if (isTwitterExport(fileList)) {
        console.log('%c=== DETECTED: TWITTER EXPORT ===', 'color: #1DA1F2; font-weight: bold');
        await processTwitterExport(unzipped);
        actualPlatform = 'twitter';
      } else {
        console.log('%c=== DETECTED: LINKEDIN EXPORT ===', 'color: #0077B5; font-weight: bold');
        await processLinkedInExport(unzipped, fileList);
        actualPlatform = 'linkedin';
      }
    }

    // Update card to uploaded state
    updatePlatformCard(actualPlatform, 'uploaded', 'Analyzed');

    // If we detected a different platform than hinted, update that card too
    if (actualPlatform !== detectedPlatform) {
      updatePlatformCard(detectedPlatform, 'error', 'Select .zip file');
    }

    // Track upload completed
    const processingTimeMs = Math.round(performance.now() - startTime);
    track('upload_completed', {
      platform: actualPlatform,
      file_size_mb: Math.round(fileSizeMB * 100) / 100,
      processing_time_ms: processingTimeMs,
      processing_time_seconds: Math.round(processingTimeMs / 1000),
    });

    setProgress(100, 'Complete!');
    setTimeout(() => showProgress(false), 500);

  } catch (err) {
    console.error('Failed to process file:', err);
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    setProgress(0, `Error: ${errorMsg}`);
    updatePlatformCard(detectedPlatform, 'error', 'Error - try again');

    // Track upload error
    track('upload_error', {
      platform: detectedPlatform,
      file_size_mb: Math.round(fileSizeMB * 100) / 100,
      error_message: errorMsg,
      processing_time_ms: Math.round(performance.now() - startTime),
    });

    // Show error with report option
    const reportLink = createErrorReportLink(err, `File processing (${file.name})`);
    progressText.innerHTML = `Error: ${escapeHtml(errorMsg)}. <a href="${reportLink}" style="color: var(--secondary); text-decoration: underline;">Report issue</a>`;
    setTimeout(() => showProgress(false), 5000);
  }
}

// === LinkedIn Processing ===
async function processLinkedInExport(unzipped: Record<string, Uint8Array>, fileList: string[]): Promise<void> {
  const data = createEmptyLinkedInData();

  // Map of file patterns to parsers
  const fileHandlers: [RegExp, (content: string) => void][] = [
    [/Positions\.csv$/i, (c) => { data.positions = parsePositions(c); }],
    [/Education\.csv$/i, (c) => { data.education = parseEducation(c); }],
    [/Connections\.csv$/i, (c) => { data.connections = parseConnections(c); }],
    [/Profile\.csv$/i, (c) => { data.profile = parseProfile(c); }],
    [/(^|\/)messages\.csv$/i, (c) => {
      console.log('%cParsing messages.csv', 'color: cyan; font-weight: bold');
      data.messages = parseMessages(c);
      console.log(`%cParsed ${data.messages.length} messages`, 'color: cyan');
    }],
    [/Publications\.csv$/i, (c) => { data.publications = parsePublications(c); }],
    [/Causes You Care About\.csv$/i, (c) => { data.causes = parseCauses(c); }],
    [/Projects\.csv$/i, (c) => { data.projects = parseProjects(c); }],
    [/Recommendations_Received\.csv$/i, (c) => { data.recommendations = parseRecommendations(c); }],
    [/(^|\/)Shares\.csv$/i, (c) => {
      console.log('%cParsing Shares.csv (posts)', 'color: magenta; font-weight: bold');
      data.shares = parseShares(c);
      console.log(`%cParsed ${data.shares.length} shares/posts`, 'color: magenta');
    }],
    [/(^|\/)Comments\.csv$/i, (c) => {
      console.log('%cParsing Comments.csv', 'color: magenta; font-weight: bold');
      data.comments = parseComments(c);
      console.log(`%cParsed ${data.comments.length} comments`, 'color: magenta');
    }],
  ];

  const jobAppPattern = /Job Applications.*\.csv$/i;
  const articlePattern = /\.html$/i;

  let processed = 0;
  let matchedFiles: string[] = [];

  for (const filename of fileList) {
    const progress = 10 + (processed / fileList.length) * 60;
    setProgress(progress, `Processing ${filename.split('/').pop()}...`);

    try {
      // Decode file content from Uint8Array to string
      const content = decodeFile(unzipped[filename]);

      let matched = false;
      for (const [pattern, handler] of fileHandlers) {
        if (pattern.test(filename)) {
          handler(content);
          matchedFiles.push(filename);
          matched = true;
          break;
        }
      }

      if (jobAppPattern.test(filename)) {
        const apps = parseJobApplications(content);
        data.jobApplications.push(...apps);
        if (!matched) matchedFiles.push(filename);
      }

      if (articlePattern.test(filename)) {
        console.log(`Parsing article: ${filename}`);
        const article = parseArticle(content, filename);
        if (article.title || article.content) {
          data.articles.push(article);
        }
        if (!matched) matchedFiles.push(filename);
      }
    } catch (err) {
      console.warn(`Failed to parse ${filename}:`, err);
    }

    processed++;
  }

  console.log('%c=== FILES MATCHED ===', 'color: orange; font-weight: bold');
  console.log(`%cMatched ${matchedFiles.length} of ${fileList.length} files`, 'color: orange');

  setProgress(75, 'Analyzing data...');
  linkedInData = data;

  console.log('%c=== LINKEDIN DATA PARSED ===', 'color: purple; font-weight: bold');
  console.log(`%cPositions: ${data.positions.length}`, 'color: purple');
  console.log(`%cEducation: ${data.education.length}`, 'color: purple');
  console.log(`%cMessages: ${data.messages.length}`, 'color: purple');
  console.log(`%cArticles: ${data.articles.length}`, 'color: purple');
  console.log(`%cShares (posts): ${data.shares.length}`, 'color: purple; font-weight: bold');
  console.log(`%cComments: ${data.comments.length}`, 'color: purple; font-weight: bold');

  // Run analysis
  analysisResult = analyzeLinkedInData(data);
  console.log('Analysis result:', analysisResult);

  setProgress(85, 'Rendering results...');
  displayResults(analysisResult);
  resultsSection.classList.add('visible');

  // Initialize LLM and run content analysis
  setProgress(90, 'Loading AI model...');
  await initLLM();

  setProgress(92, 'Analyzing content with AI...');
  const allChunks = chunkContent(
    data.messages, data.articles, data.profile, data.recommendations,
    data.publications, data.shares, data.comments
  );
  await runContentAnalysis(allChunks, 'linkedin');
}

// === Twitter Processing ===
async function processTwitterExport(unzipped: Record<string, Uint8Array>): Promise<void> {
  setProgress(10, 'Reading Twitter data files...');

  // Read relevant Twitter files into a map
  const fileContents = new Map<string, string>();
  const relevantFiles = [
    'data/tweets.js',
    'data/direct-messages.js',
    'data/profile.js',
    'data/account.js',
    'data/like.js',
  ];

  for (const filename of relevantFiles) {
    if (unzipped[filename]) {
      try {
        const content = decodeFile(unzipped[filename]);
        fileContents.set(filename, content);
        console.log(`%cRead ${filename}`, 'color: #1DA1F2');
      } catch (err) {
        console.warn(`Failed to read ${filename}:`, err);
      }
    }
  }

  setProgress(40, 'Parsing Twitter data...');

  // Parse Twitter data
  const data = parseTwitterData(fileContents);

  console.log('%c=== TWITTER DATA PARSED ===', 'color: #1DA1F2; font-weight: bold');
  console.log(`%cAccount: @${data.account?.username}`, 'color: #1DA1F2');
  console.log(`%cTweets: ${data.tweets.length}`, 'color: #1DA1F2; font-weight: bold');
  console.log(`%cDMs: ${data.directMessages.length}`, 'color: #1DA1F2');
  console.log(`%cLikes: ${data.likes.length}`, 'color: #1DA1F2');

  setProgress(60, 'Preparing results...');

  // Display Twitter-specific results
  displayTwitterResults(data);
  resultsSection.classList.add('visible');

  // Initialize LLM and run content analysis
  setProgress(80, 'Loading AI model...');
  await initLLM();

  setProgress(85, 'Analyzing content with AI...');
  const allChunks = chunkTwitterContent(data);
  await runContentAnalysis(allChunks, 'twitter');
}

// === PDF Visa Document Processing ===
async function processPDFFile(file: File, docType: 'ds160' | 'i129'): Promise<void> {
  const startTime = performance.now();
  const fileSizeMB = file.size / 1024 / 1024;

  console.log('%c========================================', 'color: purple; font-weight: bold');
  console.log('%c PROCESSING PDF: ' + file.name, 'color: purple; font-weight: bold');
  console.log('%c Document type: ' + docType.toUpperCase(), 'color: purple');
  console.log('%c File size: ' + fileSizeMB.toFixed(2) + ' MB', 'color: purple');
  console.log('%c========================================', 'color: purple; font-weight: bold');

  // Track upload started
  track('pdf_upload_started', {
    document_type: docType,
    file_size_mb: Math.round(fileSizeMB * 100) / 100,
  });

  updateVisaDocCard(docType, 'processing', 'Reading PDF...');

  try {
    // Check WebGPU support for OCR
    const hasWebGPU = await checkWebGPUSupport();
    console.log(`%cWebGPU available: ${hasWebGPU}`, 'color: purple');

    // Parse the visa document
    const { extraction, parsed } = await parseVisaDocument(file, (ocrStatus) => {
      // Update OCR progress indicator
      showOCRProgress(true, ocrStatus.message);
      console.log(`%cOCR: ${ocrStatus.stage} - ${ocrStatus.message}`, 'color: orange');
    });

    showOCRProgress(false);

    console.log('%c=== PDF EXTRACTION RESULT ===', 'color: green; font-weight: bold');
    console.log(`%cPages: ${extraction.pageCount}`, 'color: green');
    console.log(`%cIs scanned: ${extraction.isScanned}`, 'color: green');
    console.log(`%cOCR used: ${extraction.ocrUsed}`, 'color: green');
    console.log(`%cExtraction method: ${extraction.extractionMethod}`, 'color: green');
    console.log(`%cText length: ${extraction.text.length} chars`, 'color: green');

    // Check if document type matches expected
    const detection = detectDocumentType(extraction.text);
    console.log(`%cDetected type: ${detection.type} (confidence: ${detection.confidence})`, 'color: green');

    // Warn if detected type doesn't match expected
    if (detection.type !== 'unknown') {
      const expectedType = docType === 'ds160' ? 'DS-160' : 'I-129';
      if (detection.type !== expectedType) {
        console.warn(`%cWarning: Expected ${expectedType} but detected ${detection.type}`, 'color: orange');
      }
    }

    // Store parsed data
    if (docType === 'ds160' && parsed.type === 'DS-160') {
      ds160Data = parsed.data as DS160Data;
      const fullName = [ds160Data.personal?.givenName, ds160Data.personal?.surname].filter(Boolean).join(' ') || 'N/A';
      console.log('%c=== DS-160 DATA PARSED ===', 'color: blue; font-weight: bold');
      console.log(`%cConfirmation: ${ds160Data.confirmationNumber || 'N/A'}`, 'color: blue');
      console.log(`%cName: ${fullName}`, 'color: blue');
      console.log(`%cEmployment entries: ${ds160Data.employment?.length || 0}`, 'color: blue');
      console.log(`%cEducation entries: ${ds160Data.education?.length || 0}`, 'color: blue');
      console.log(`%cSocial media: ${ds160Data.socialMedia?.length || 0}`, 'color: blue');
    } else if (docType === 'i129' && parsed.type === 'I-129') {
      i129Data = parsed.data as I129Data;
      const beneficiaryName = [i129Data.beneficiary?.givenName, i129Data.beneficiary?.surname].filter(Boolean).join(' ') || 'N/A';
      console.log('%c=== I-129 DATA PARSED ===', 'color: blue; font-weight: bold');
      console.log(`%cReceipt: ${i129Data.receiptNumber || 'N/A'}`, 'color: blue');
      console.log(`%cPetitioner: ${i129Data.petitioner?.companyName || 'N/A'}`, 'color: blue');
      console.log(`%cBeneficiary: ${beneficiaryName}`, 'color: blue');
      console.log(`%cJob title: ${i129Data.job?.title || 'N/A'}`, 'color: blue');
    } else {
      console.warn(`%cParsed type (${parsed.type}) doesn't match expected (${docType})`, 'color: orange');
    }

    // Update card status
    const statusMsg = extraction.ocrUsed
      ? `Analyzed (OCR: ${extraction.extractionMethod})`
      : 'Analyzed';
    updateVisaDocCard(docType, 'uploaded', statusMsg);

    // Track success
    const processingTimeMs = Math.round(performance.now() - startTime);
    track('pdf_upload_completed', {
      document_type: docType,
      detected_type: detection.type,
      confidence: detection.confidence,
      file_size_mb: Math.round(fileSizeMB * 100) / 100,
      processing_time_ms: processingTimeMs,
      is_scanned: extraction.isScanned,
      ocr_used: extraction.ocrUsed,
      extraction_method: extraction.extractionMethod,
      text_length: extraction.text.length,
    });

    // Run cross-validation if we have both LinkedIn data and visa doc
    if (linkedInData && (ds160Data || i129Data)) {
      runCrossValidation();
    }

  } catch (err) {
    console.error('PDF processing failed:', err);
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    showOCRProgress(false);
    updateVisaDocCard(docType, 'error', 'Error - try again');

    // Track error
    track('pdf_upload_error', {
      document_type: docType,
      error_message: errorMsg,
      processing_time_ms: Math.round(performance.now() - startTime),
    });

    // Show error message
    alert(`Failed to process PDF: ${errorMsg}\n\nPlease try again or report this issue.`);
  }
}

// === Cross-Validation ===
function runCrossValidation(): void {
  if (!linkedInData) {
    console.log('No LinkedIn data for cross-validation');
    return;
  }

  // Build parsed visa document from available data
  let visaDoc: ParsedVisaDocument | null = null;
  if (ds160Data) {
    visaDoc = { type: 'DS-160', data: ds160Data };
  } else if (i129Data) {
    visaDoc = { type: 'I-129', data: i129Data };
  }

  if (!visaDoc) {
    console.log('No visa document data for cross-validation');
    return;
  }

  console.log('%c=== RUNNING CROSS-VALIDATION ===', 'color: magenta; font-weight: bold');

  // Transform LinkedIn data to the format expected by crossValidator
  const linkedInForValidation = {
    positions: linkedInData.positions.map(p => ({
      companyName: p.companyName,
      title: p.title,
      startDate: p.startedOn ? { month: p.startedOn.getMonth() + 1, year: p.startedOn.getFullYear() } : undefined,
      endDate: p.finishedOn ? { month: p.finishedOn.getMonth() + 1, year: p.finishedOn.getFullYear() } : undefined,
    })),
    education: linkedInData.education.map(e => ({
      schoolName: e.schoolName,
      degreeName: e.degreeName,
      startDate: e.startDate ? { year: e.startDate.getFullYear() } : undefined,
      endDate: e.endDate ? { year: e.endDate.getFullYear() } : undefined,
    })),
    profile: linkedInData.profile ? {
      twitterHandles: [], // LinkedIn doesn't store twitter handles in the export
    } : undefined,
  };

  // Run cross-validation
  crossValidationResult = crossValidate(linkedInForValidation, null, visaDoc);

  console.log('%c=== CROSS-VALIDATION RESULT ===', 'color: magenta; font-weight: bold');
  console.log(`%cTotal discrepancies: ${crossValidationResult.totalDiscrepancies}`, 'color: magenta');
  console.log(`%cCritical: ${crossValidationResult.criticalCount}`, 'color: red');
  console.log(`%cWarnings: ${crossValidationResult.warningCount}`, 'color: orange');
  console.log(`%cInfo: ${crossValidationResult.infoCount}`, 'color: green');
  console.log(`%cCoverage score: ${(crossValidationResult.coverageScore * 100).toFixed(0)}%`, 'color: magenta');

  // Track cross-validation
  track('cross_validation_completed', {
    total_discrepancies: crossValidationResult.totalDiscrepancies,
    critical_count: crossValidationResult.criticalCount,
    warning_count: crossValidationResult.warningCount,
    info_count: crossValidationResult.infoCount,
    coverage_score: crossValidationResult.coverageScore,
    linkedin_coverage: crossValidationResult.linkedInCoverage,
    visa_doc_coverage: crossValidationResult.visaDocCoverage,
    timeline_gaps: crossValidationResult.timelineGaps.length,
    overlapping_employment: crossValidationResult.overlappingEmployment.length,
  });

  // Display results
  displayCrossValidationResults(crossValidationResult);
}

function displayCrossValidationResults(result: CrossValidationResult): void {
  // Show the cross-validation section
  crossValidationSection.classList.remove('hidden');

  // Update summary stats
  cvCritical.textContent = String(result.criticalCount);
  cvWarnings.textContent = String(result.warningCount);
  cvInfo.textContent = String(result.infoCount);
  cvCoverage.textContent = `${(result.coverageScore * 100).toFixed(0)}%`;

  // Build recommendations HTML
  let recsHtml = '';
  if (result.totalDiscrepancies === 0) {
    recsHtml = `
      <div class="cv-recommendation success">
        No discrepancies found. Your LinkedIn profile matches your visa documents.
      </div>
    `;
  } else {
    for (const rec of result.recommendations) {
      recsHtml += `<div class="cv-recommendation">${escapeHtml(rec)}</div>`;
    }
  }
  crossValidationRecommendations.innerHTML = recsHtml;

  // Build discrepancies HTML
  let discrepanciesHtml = '';

  if (result.totalDiscrepancies > 0) {
    // All discrepancies sorted by severity
    const allDiscrepancies = [
      ...result.employmentDiscrepancies,
      ...result.educationDiscrepancies,
      ...result.socialMediaDiscrepancies,
      ...result.personalDiscrepancies,
    ].sort((a, b) => {
      const severityOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });

    for (let i = 0; i < allDiscrepancies.length; i++) {
      const d = allDiscrepancies[i];
      discrepanciesHtml += renderDiscrepancy(d, i);
    }
  }

  discrepancyList.innerHTML = discrepanciesHtml;
}

function renderDiscrepancy(d: Discrepancy, index: number): string {
  const severityLabel = getSeverityLabel(d.severity);
  const discrepancyId = `discrepancy-${index}`;

  // Build the comparison display
  let comparisonHtml = '';
  if (d.linkedInValue || d.visaDocValue) {
    comparisonHtml = `
      <div class="discrepancy-comparison">
        <div class="comparison-side">
          <span class="comparison-label">LinkedIn</span>
          <span class="comparison-value">${escapeHtml(d.linkedInValue || 'Not found')}</span>
        </div>
        <div class="comparison-side">
          <span class="comparison-label">Visa Doc</span>
          <span class="comparison-value">${escapeHtml(d.visaDocValue || 'Not found')}</span>
        </div>
      </div>
    `;
  }

  // Context (company name, institution, etc.)
  let companyHtml = '';
  if (d.companyName) {
    companyHtml = `<div class="discrepancy-company">${escapeHtml(d.companyName)}</div>`;
  } else if (d.institutionName) {
    companyHtml = `<div class="discrepancy-company">${escapeHtml(d.institutionName)}</div>`;
  }

  // Days difference for date mismatches
  let daysHtml = '';
  if (d.daysDifference !== undefined && d.daysDifference > 0) {
    daysHtml = ` (${d.daysDifference} days)`;
  }

  return `
    <div class="discrepancy-item" data-discrepancy-id="${discrepancyId}" data-type="${d.type}" data-severity="${d.severity}">
      <div class="discrepancy-header">
        <span class="discrepancy-severity ${d.severity}">${severityLabel}</span>
        <span class="discrepancy-type">${formatDiscrepancyType(d.type)}${daysHtml}</span>
      </div>
      ${companyHtml}
      ${comparisonHtml}
      <div class="discrepancy-recommendation">${escapeHtml(d.recommendation)}</div>
      <div class="feedback-row" data-discrepancy-id="${discrepancyId}">
        <span class="feedback-prompt">Was this helpful?</span>
        <button class="feedback-btn helpful" data-feedback="helpful" data-discrepancy-id="${discrepancyId}">👍 Yes</button>
        <button class="feedback-btn not-helpful" data-feedback="not-helpful" data-discrepancy-id="${discrepancyId}">👎 No</button>
      </div>
    </div>
  `;
}

function formatDiscrepancyType(type: string): string {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// === Unified Content Analysis ===
async function runContentAnalysis(allChunks: ContentChunk[], platform: string): Promise<void> {
  if (!client) {
    console.warn('LLM not available, skipping content analysis');
    displayNoLLMMessage();
    return;
  }

  try {
    const chunksByType = allChunks.reduce((acc, c) => {
      acc[c.type] = (acc[c.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    console.log(`=== Content Chunks (${platform}) ===`);
    console.log(`Total chunks: ${allChunks.length}`);
    console.log('By type:', chunksByType);

    const relevantChunks = preFilterChunks(allChunks);
    console.log(`After pre-filter: ${relevantChunks.length} chunks to analyze`);

    if (relevantChunks.length === 0) {
      displayAllClear(allChunks.length, platform);
      return;
    }

    llmStatus.textContent = `Analyzing ${relevantChunks.length} content pieces with AI...`;
    llmOutput.innerHTML = `
      <div class="content-scan-progress">
        <div class="scan-spinner"></div>
        <p>AI is reviewing your ${platform === 'twitter' ? 'Twitter' : 'LinkedIn'} content...</p>
        <p class="scan-details">This may take a few minutes for large profiles.</p>
      </div>
    `;

    llmAnalysisResult = await analyzeContentWithLLM(
      client,
      relevantChunks,
      (analyzed, total, flag) => {
        const pct = Math.round((analyzed / total) * 100);
        llmStatus.textContent = `AI analyzing: ${pct}% (${analyzed}/${total} checks)`;
        setProgress(85 + (pct * 0.14), `AI Analysis: ${pct}%`);

        if (flag) {
          console.log(`Flag found: ${flag.category}`, flag.content.substring(0, 100));
        }
      }
    );

    displayLLMResults(llmAnalysisResult, allChunks.length, platform);

    const criticalCount = llmAnalysisResult.flags.filter(f => f.severity === 'red').length;
    const warningCount = llmAnalysisResult.flags.filter(f => f.severity === 'orange').length;

    // Track analysis completed
    track('analysis_completed', {
      platform,
      total_chunks: allChunks.length,
      analyzed_chunks: llmAnalysisResult.chunksAnalyzed,
      critical_flags: criticalCount,
      warning_flags: warningCount,
      total_flags: llmAnalysisResult.flags.length,
    });

    const currentCritical = parseInt(statCritical.textContent || '0');
    const currentWarnings = parseInt(statWarnings.textContent || '0');
    statCritical.textContent = String(currentCritical + criticalCount);
    statWarnings.textContent = String(currentWarnings + warningCount);

  } catch (err) {
    console.error('Content analysis failed:', err);
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    const reportLink = createErrorReportLink(err, `AI content analysis (${platform})`);
    llmOutput.innerHTML = `
      <div class="content-scan-error">
        AI analysis failed: ${escapeHtml(errorMsg)}
        <br><br>
        <a href="${reportLink}" style="color: inherit; text-decoration: underline;">Report this issue</a>
      </div>
    `;
  }
}

// === Twitter Results Display ===
function displayTwitterResults(data: TwitterData): void {
  // Update risk badge (Twitter doesn't have structured analysis like LinkedIn)
  riskBadge.textContent = 'Twitter Data';
  riskBadge.className = 'risk-label green';

  // Stats
  const originalTweets = data.tweets.filter(t => !t.isRetweet).length;
  statCritical.textContent = '0';
  statWarnings.textContent = '0';
  statPositive.textContent = String(originalTweets);
  statYears.textContent = data.account?.createdAt
    ? `${Math.round((Date.now() - data.account.createdAt.getTime()) / (365 * 24 * 60 * 60 * 1000))}y`
    : '-';

  // Update section titles for Twitter context
  const flagsSection = document.getElementById('flags-section');
  if (flagsSection) {
    const title = flagsSection.querySelector('.section-title');
    if (title) title.textContent = 'Content Analysis';
  }

  // Display profile info in flags area
  flagsList.innerHTML = `
    <div class="twitter-profile-summary">
      <div class="profile-header">
        <strong>@${escapeHtml(data.account?.username || 'unknown')}</strong>
        <span class="profile-name">${escapeHtml(data.account?.displayName || '')}</span>
      </div>
      ${data.profile?.bio ? `<p class="profile-bio">${escapeHtml(data.profile.bio)}</p>` : ''}
      ${data.profile?.location ? `<p class="profile-location">${escapeHtml(data.profile.location)}</p>` : ''}
      <div class="profile-stats">
        <span><strong>${originalTweets}</strong> original tweets</span>
        <span><strong>${data.tweets.filter(t => t.isRetweet).length}</strong> retweets</span>
        <span><strong>${data.directMessages.length}</strong> DMs</span>
        <span><strong>${data.likes.length}</strong> likes</span>
      </div>
    </div>
  `;

  // Hide timeline section for Twitter (no employment history)
  timelineList.innerHTML = `
    <p style="color: var(--secondary); padding: 20px;">
      Twitter exports don't include employment history. Upload your LinkedIn export for career timeline analysis.
    </p>
  `;
}


// === Results Display ===
function displayResults(result: VisaAnalysisResult): void {
  // Risk label
  const risk = result.summary.overallRiskLevel;
  riskBadge.textContent = risk === 'red' ? 'High Risk' : risk === 'orange' ? 'Moderate Risk' : 'Low Risk';
  riskBadge.className = `risk-label ${risk}`;

  // Stats
  statCritical.textContent = String(result.summary.criticalFlags);
  statWarnings.textContent = String(result.summary.warningFlags);
  statPositive.textContent = String(result.summary.positiveIndicators);

  const usYears = Math.round(result.timeline.totalUSExperienceDays / 365);
  statYears.textContent = `${usYears}y`;

  // Red flags
  displayFlags(result.redFlags);

  // Timeline
  displayTimeline(result.timeline.entries, result.timeline.gaps);
}

function displayFlags(flags: RedFlag[]): void {
  flagsList.innerHTML = '';

  if (flags.length === 0) {
    flagsList.innerHTML = '<p style="color: #166534;">No issues found.</p>';
    return;
  }

  // Sort by severity (red first, then orange)
  const sorted = [...flags].sort((a, b) => {
    const order = { red: 0, orange: 1, green: 2 };
    return order[a.severity] - order[b.severity];
  });

  for (const flag of sorted) {
    const item = document.createElement('div');
    item.className = 'flag-item';
    const severityLabel = flag.severity === 'red' ? 'Critical' : flag.severity === 'orange' ? 'Warning' : 'Info';
    item.innerHTML = `
      <div class="flag-header">
        <span class="flag-severity ${flag.severity}">${severityLabel}</span>
        <span class="flag-title">${escapeHtml(flag.description)}</span>
      </div>
      <div class="flag-evidence">${escapeHtml(truncate(flag.evidence, 200))}</div>
      <div class="flag-recommendation">${escapeHtml(flag.recommendation)}</div>
    `;
    flagsList.appendChild(item);
  }
}

function displayTimeline(entries: TimelineEntry[], gaps: TimelineEntry[]): void {
  timelineList.innerHTML = '';

  // Filter entries with valid start dates and sort by date descending
  const validEntries = entries.filter(e => e.startDate !== null);
  validEntries.sort((a, b) => (b.startDate?.getTime() || 0) - (a.startDate?.getTime() || 0));

  for (const entry of validEntries) {
    // Skip gap entries - they're shown separately
    if (entry.type === 'gap') continue;

    const div = document.createElement('div');
    div.className = 'timeline-item';
    div.innerHTML = `
      <div class="timeline-date">${entry.startDate ? formatDate(entry.startDate) : 'Unknown'} - ${entry.endDate ? formatDate(entry.endDate) : 'Present'}</div>
      <div class="timeline-content">
        <div class="timeline-role">${escapeHtml(entry.label)}</div>
        <div class="timeline-org">${escapeHtml(entry.organization)}${entry.location ? ` · ${escapeHtml(entry.location)}` : ''}</div>
      </div>
    `;
    timelineList.appendChild(div);
  }

  // Show gaps if any
  if (gaps.length > 0) {
    const gapHeader = document.createElement('div');
    gapHeader.style.cssText = 'margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee; font-size: 13px; color: #666; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em;';
    gapHeader.textContent = 'Gaps';
    timelineList.appendChild(gapHeader);

    for (const gap of gaps) {
      if (!gap.startDate) continue;
      const div = document.createElement('div');
      div.className = 'timeline-item';
      div.innerHTML = `
        <div class="timeline-date">${formatDate(gap.startDate)}</div>
        <div class="timeline-gap">
          ${Math.round(gap.durationDays)} days${gap.endDate ? ` (${formatDate(gap.startDate)} – ${formatDate(gap.endDate)})` : ''}
        </div>
      `;
      timelineList.appendChild(div);
    }
  }
}

function displayNoLLMMessage(): void {
  llmOutput.innerHTML = `
    <div class="content-scan-info">
      <p>AI model is loading in the background.</p>
      <p class="scan-details">Content analysis will run when the model is ready. You can still ask questions once loading completes.</p>
    </div>
  `;
  llmStatus.textContent = 'Model loading...';
}

function displayAllClear(totalChunks: number, platform: string = 'linkedin'): void {
  const platformName = platform === 'twitter' ? 'Twitter' : 'LinkedIn';
  const contentTypes = platform === 'twitter'
    ? 'tweets, DMs, and likes'
    : 'profile, posts, messages, and articles';

  llmOutput.innerHTML = `
    <div class="content-scan-success">
      <div class="scan-icon">✓</div>
      <h3>All Clear</h3>
      <p>No potentially concerning content found in your ${platformName} data.</p>
      <p class="scan-details">Scanned ${totalChunks} content pieces including your ${contentTypes}.</p>
    </div>
  `;
  llmStatus.textContent = 'Content scan complete - no issues found';
}

function displayLLMResults(result: LLMAnalysisResult, totalChunks: number, platform: string = 'linkedin'): void {
  if (result.flags.length === 0) {
    displayAllClear(totalChunks, platform);
    return;
  }

  const redFlags = result.flags.filter(f => f.severity === 'red');
  const orangeFlags = result.flags.filter(f => f.severity === 'orange');

  let html = `
    <div class="content-scan-summary">
      <p>${escapeHtml(result.summary)}</p>
      <p class="scan-details">Analyzed ${result.chunksAnalyzed} of ${totalChunks} content pieces.</p>
    </div>
    <div class="content-flags">
  `;

  // Red flags first
  let flagIndex = 0;
  for (const flag of redFlags) {
    html += renderLLMFlag(flag, flagIndex++);
  }

  // Then orange flags
  for (const flag of orangeFlags) {
    html += renderLLMFlag(flag, flagIndex++);
  }

  html += '</div>';
  llmOutput.innerHTML = html;
  llmStatus.textContent = `AI scan complete - ${result.flags.length} item${result.flags.length !== 1 ? 's' : ''} to review`;
}

function renderLLMFlag(flag: LLMFlag, index: number): string {
  const severityClass = flag.severity === 'red' ? 'critical' : 'warning';
  const icon = flag.severity === 'red' ? '!' : '?';
  const flagId = `flag-${index}`;

  return `
    <div class="content-flag ${severityClass}" data-flag-id="${flagId}" data-flag-category="${escapeHtml(flag.category)}" data-flag-severity="${flag.severity}">
      <div class="content-flag-header">
        <div class="flag-icon">${icon}</div>
        <div class="flag-category">${escapeHtml(flag.category)}</div>
      </div>
      <div class="flag-content">${escapeHtml(flag.content)}</div>
      <div class="flag-recommendation">${escapeHtml(flag.suggestion)}</div>
      <div class="feedback-row" data-flag-id="${flagId}">
        <span class="feedback-prompt">Was this accurate?</span>
        <button class="feedback-btn helpful" data-feedback="accurate" data-flag-id="${flagId}">👍 Yes</button>
        <button class="feedback-btn not-accurate" data-feedback="inaccurate" data-flag-id="${flagId}">👎 No</button>
      </div>
      <div class="feedback-form" id="feedback-form-${flagId}">
        <textarea placeholder="What was inaccurate? This helps us improve. (optional)" id="feedback-text-${flagId}"></textarea>
        <button class="feedback-submit" data-flag-id="${flagId}">Submit feedback</button>
      </div>
    </div>
  `;
}

// === LLM Q&A Integration ===
async function initLLM(): Promise<void> {
  const modelStartTime = performance.now();
  llmStatus.textContent = 'Loading model...';
  llmSend.disabled = true;

  // Track model download started
  track('model_download_started', {
    webgpu_available: 'gpu' in navigator,
  });

  try {
    client = createClient({
      engine: 'webllm',
      manifestUrl: '/models.json',
      preload: { strategy: 'speed' },
      onProgress: (event) => {
        if (event.phase === 'download') {
          llmStatus.textContent = event.message || 'Downloading model...';
        } else if (event.phase === 'load') {
          llmStatus.textContent = 'Loading into GPU...';
        } else if (event.phase === 'ready') {
          llmStatus.textContent = 'Model ready';
          llmSend.disabled = false;
        }
      },
    });

    // Wait for preload
    await client.waitForPreload();
    llmStatus.textContent = 'Model ready';
    llmSend.disabled = false;

    // Track model ready
    const loadTimeMs = Math.round(performance.now() - modelStartTime);
    track('model_ready', {
      load_time_ms: loadTimeMs,
      load_time_seconds: Math.round(loadTimeMs / 1000),
    });
  } catch (err) {
    console.error('LLM init failed:', err);
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';

    // Track model error
    track('model_error', {
      error_message: errorMsg,
      load_time_ms: Math.round(performance.now() - modelStartTime),
    });

    const reportLink = createErrorReportLink(err, 'AI model initialization');
    llmStatus.innerHTML = `Model unavailable. <a href="${reportLink}" style="color: inherit; text-decoration: underline; font-size: inherit;">Report issue</a>`;
  }
}

async function askLLM(question: string): Promise<void> {
  if (!client || !analysisResult) return;

  // Track question asked
  track('llm_question_asked', {
    question_length: question.length,
    is_quick_question: question.length > 50, // Quick questions are longer preset prompts
  });

  llmSend.disabled = true;
  llmOutput.textContent = '';
  llmOutput.classList.add('streaming');

  // Build context from analysis
  const context = buildLLMContext();
  let fullResponse = '';

  try {
    const stream = client.chatStream({
      model: 'auto',
      messages: [
        {
          role: 'system',
          content: `You are a US visa interview preparation coach. Your role is to help the user prepare answers for potential questions from a consular officer or background check interviewer.

CONTEXT: The user is preparing for a visa interview (like H1B, B1/B2, or similar). Consular officers may ask about:
- Employment gaps or overlaps
- Side businesses while employed
- Job changes and reasons for switching
- Intent to return to home country
- Ties to home country

YOUR TASK: Help the user craft honest, clear, and non-defensive answers that address potential concerns. Focus on:
1. What the visa officer might ask about this issue
2. How to explain it truthfully and positively
3. What documentation might help support the explanation
4. What NOT to say (things that could raise more concerns)

Be specific to visa interviews. Keep answers concise and practical. Use markdown formatting for clarity.

User's LinkedIn Profile Data:
${context}`,
        },
        { role: 'user', content: question },
      ],
    });

    for await (const event of stream) {
      if (event.type === 'delta' && event.deltaText) {
        fullResponse += event.deltaText;
        llmOutput.textContent = fullResponse;
      }
    }

    // Render markdown after streaming completes
    llmOutput.innerHTML = marked.parse(fullResponse) + `
      <div class="answer-feedback" data-question-type="${question.length > 50 ? 'quick' : 'custom'}">
        <span class="feedback-prompt">Was this helpful?</span>
        <button class="feedback-btn helpful answer-feedback-btn" data-feedback="helpful">👍 Yes</button>
        <button class="feedback-btn not-accurate answer-feedback-btn" data-feedback="not-helpful">👎 No</button>
      </div>
      <div class="feedback-form" id="answer-feedback-form">
        <textarea placeholder="What could be better? (optional)" id="answer-feedback-text"></textarea>
        <button class="feedback-submit answer-feedback-submit">Submit feedback</button>
      </div>
    `;
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    const reportLink = createErrorReportLink(err, 'AI Q&A');
    llmOutput.innerHTML = `<div style="color: var(--danger);">Error: ${escapeHtml(errorMsg)}</div><p style="margin-top: 12px; font-size: 0.8125rem;"><a href="${reportLink}" style="color: var(--secondary); text-decoration: underline;">Report this issue</a></p>`;
  }

  llmOutput.classList.remove('streaming');
  llmSend.disabled = false;
}

function buildLLMContext(): string {
  if (!analysisResult || !linkedInData) return '';

  const lines: string[] = [];

  // Profile summary
  if (linkedInData.profile) {
    lines.push(`Name: ${linkedInData.profile.firstName} ${linkedInData.profile.lastName}`);
    lines.push(`Current Headline: ${linkedInData.profile.headline}`);
    lines.push(`Location: ${linkedInData.profile.geoLocation || linkedInData.profile.address || 'Not specified'}`);
  }

  // Positions with more detail
  lines.push('\n=== EMPLOYMENT HISTORY ===');
  for (const pos of linkedInData.positions) {
    const start = pos.startedOn?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) || 'Unknown';
    const end = pos.finishedOn?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) || 'Present';
    lines.push(`${pos.title} at ${pos.companyName}`);
    lines.push(`  Period: ${start} - ${end}`);
    lines.push(`  Location: ${pos.location || 'Not specified'}`);
  }

  // Education
  lines.push('\n=== EDUCATION ===');
  for (const edu of linkedInData.education) {
    const start = edu.startDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) || '';
    const end = edu.endDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) || '';
    lines.push(`${edu.degreeName || 'Degree'} from ${edu.schoolName} (${start} - ${end})`);
  }

  // Timeline stats
  lines.push('\n=== VISA-RELEVANT STATS ===');
  lines.push(`Total US Experience: ${Math.round(analysisResult.timeline.totalUSExperienceDays / 365)} years`);
  lines.push(`Total Non-US Experience: ${Math.round(analysisResult.timeline.totalHomeCountryExperienceDays / 365)} years`);
  lines.push(`Employment Gaps: ${analysisResult.timeline.gaps.length}`);
  lines.push(`Position Overlaps: ${analysisResult.timeline.overlaps.length}`);

  // Flagged issues - this is critical for Q&A
  if (analysisResult.redFlags.length > 0) {
    lines.push('\n=== FLAGGED ISSUES (visa officer may ask about) ===');
    for (const flag of analysisResult.redFlags) {
      lines.push(`\n[${flag.severity.toUpperCase()}] ${flag.category.replace(/_/g, ' ')}`);
      lines.push(`Issue: ${flag.description}`);
      lines.push(`Details: ${flag.evidence}`);
    }
  } else {
    lines.push('\n=== NO MAJOR ISSUES FLAGGED ===');
  }

  return lines.join('\n');
}

// === Utility Functions ===

// Create a pre-filled email link for error reporting
function createErrorReportLink(error: Error | unknown, context: string): string {
  const errorMessage = error instanceof Error ? error.message : 'Unknown error';
  const errorStack = error instanceof Error ? error.stack?.slice(0, 500) : '';
  const userAgent = navigator.userAgent;
  const webGPU = 'gpu' in navigator ? 'available' : 'unavailable';

  const subject = encodeURIComponent(`Skreen Error: ${context}`);
  const body = encodeURIComponent(
`Hi Skreen team,

I encountered an error while using the app.

Context: ${context}
Error: ${errorMessage}

Technical details:
- Browser: ${userAgent}
- WebGPU: ${webGPU}
${errorStack ? `- Stack: ${errorStack}` : ''}

[Please describe what you were doing when this happened]
`
  );

  return `mailto:hello@withskreen.com?subject=${subject}&body=${body}`;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.substring(0, maxLen - 3) + '...';
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// === Event Listeners ===

// Platform card file inputs
linkedinInput.addEventListener('change', () => {
  const file = linkedinInput.files?.[0];
  if (file) {
    processZipFile(file, 'linkedin');
    linkedinInput.value = ''; // Reset for re-upload
  }
});

twitterInput.addEventListener('change', () => {
  const file = twitterInput.files?.[0];
  if (file) {
    processZipFile(file, 'twitter');
    twitterInput.value = ''; // Reset for re-upload
  }
});

// Visa document PDF inputs
ds160Input.addEventListener('change', () => {
  const file = ds160Input.files?.[0];
  if (file) {
    processPDFFile(file, 'ds160');
    ds160Input.value = ''; // Reset for re-upload
  }
});

i129Input.addEventListener('change', () => {
  const file = i129Input.files?.[0];
  if (file) {
    processPDFFile(file, 'i129');
    i129Input.value = ''; // Reset for re-upload
  }
});

// Legacy drag-drop area (for backwards compatibility)
uploadArea.addEventListener('click', () => fileInput.click());

uploadArea.addEventListener('dragover', (e) => {
  e.preventDefault();
  uploadArea.classList.add('dragover');
});

uploadArea.addEventListener('dragleave', () => {
  uploadArea.classList.remove('dragover');
});

uploadArea.addEventListener('drop', (e) => {
  e.preventDefault();
  uploadArea.classList.remove('dragover');
  const file = e.dataTransfer?.files[0];
  if (file && file.name.endsWith('.zip')) {
    processZipFile(file);
  }
});

fileInput.addEventListener('change', () => {
  const file = fileInput.files?.[0];
  if (file) {
    processZipFile(file);
  }
});

// Global drag-drop on body to show drop zone
document.body.addEventListener('dragover', (e) => {
  e.preventDefault();
  uploadArea.classList.remove('hidden');
});

document.body.addEventListener('dragleave', (e) => {
  if (e.relatedTarget === null) {
    uploadArea.classList.add('hidden');
  }
});

document.body.addEventListener('drop', (e) => {
  e.preventDefault();
  uploadArea.classList.add('hidden');
  const file = e.dataTransfer?.files[0];
  if (file && file.name.endsWith('.zip')) {
    processZipFile(file);
  }
});

llmSend.addEventListener('click', () => {
  const question = llmInput.value.trim();
  if (question) {
    askLLM(question);
    llmInput.value = '';
  }
});

llmInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && !llmSend.disabled) {
    const question = llmInput.value.trim();
    if (question) {
      askLLM(question);
      llmInput.value = '';
    }
  }
});

quickQuestions.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;
  if (target.classList.contains('quick-question')) {
    const question = target.dataset.q;
    if (question) {
      askLLM(question);
    }
  }
});

// === Feedback Event Handlers ===

// Handle feedback button clicks (using event delegation on llmOutput)
llmOutput.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;

  // Handle flag feedback buttons
  if (target.classList.contains('feedback-btn') && target.dataset.flagId) {
    const flagId = target.dataset.flagId;
    const feedback = target.dataset.feedback;
    const flagEl = target.closest('.content-flag') as HTMLElement;
    const category = flagEl?.dataset.flagCategory || 'unknown';
    const severity = flagEl?.dataset.flagSeverity || 'unknown';

    // Mark this button as selected
    const row = target.closest('.feedback-row');
    row?.querySelectorAll('.feedback-btn').forEach(btn => btn.classList.remove('selected'));
    target.classList.add('selected');

    // Track the feedback
    track('flag_feedback', {
      flag_id: flagId,
      flag_category: category,
      flag_severity: severity,
      feedback_type: feedback,
    });

    // Show feedback form if "inaccurate"
    const form = document.getElementById(`feedback-form-${flagId}`);
    if (feedback === 'inaccurate' && form) {
      form.classList.add('visible');
    } else if (form) {
      form.classList.remove('visible');
    }
  }

  // Handle flag feedback form submit
  if (target.classList.contains('feedback-submit') && target.dataset.flagId) {
    const flagId = target.dataset.flagId;
    const textarea = document.getElementById(`feedback-text-${flagId}`) as HTMLTextAreaElement;
    const comment = textarea?.value?.trim() || '';
    const flagEl = target.closest('.content-flag') as HTMLElement;
    const category = flagEl?.dataset.flagCategory || 'unknown';
    const severity = flagEl?.dataset.flagSeverity || 'unknown';

    // Track the comment submission
    track('flag_feedback_comment', {
      flag_id: flagId,
      flag_category: category,
      flag_severity: severity,
      has_comment: comment.length > 0,
      comment_length: comment.length,
    });

    // Replace form with thank you message
    const form = document.getElementById(`feedback-form-${flagId}`);
    if (form) {
      form.innerHTML = '<div class="feedback-thanks">Thanks for your feedback!</div>';
    }
  }

  // Handle answer feedback buttons
  if (target.classList.contains('answer-feedback-btn')) {
    const feedback = target.dataset.feedback;
    const answerFeedback = target.closest('.answer-feedback') as HTMLElement;
    const questionType = answerFeedback?.dataset.questionType || 'unknown';

    // Mark this button as selected
    answerFeedback?.querySelectorAll('.feedback-btn').forEach(btn => btn.classList.remove('selected'));
    target.classList.add('selected');

    // Track the feedback
    track('answer_feedback', {
      question_type: questionType,
      feedback_type: feedback,
    });

    // Show feedback form if "not-helpful"
    const form = document.getElementById('answer-feedback-form');
    if (feedback === 'not-helpful' && form) {
      form.classList.add('visible');
    } else if (form) {
      form.classList.remove('visible');
    }
  }

  // Handle answer feedback form submit
  if (target.classList.contains('answer-feedback-submit')) {
    const textarea = document.getElementById('answer-feedback-text') as HTMLTextAreaElement;
    const comment = textarea?.value?.trim() || '';
    const answerFeedback = llmOutput.querySelector('.answer-feedback') as HTMLElement;
    const questionType = answerFeedback?.dataset.questionType || 'unknown';

    // Track the comment submission
    track('answer_feedback_comment', {
      question_type: questionType,
      has_comment: comment.length > 0,
      comment_length: comment.length,
    });

    // Replace form with thank you message
    const form = document.getElementById('answer-feedback-form');
    if (form) {
      form.innerHTML = '<div class="feedback-thanks">Thanks for your feedback!</div>';
    }
  }
});

// Handle cross-validation discrepancy feedback (using event delegation)
discrepancyList.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;

  // Handle discrepancy feedback buttons
  if (target.classList.contains('feedback-btn') && target.dataset.discrepancyId) {
    const discrepancyId = target.dataset.discrepancyId;
    const feedback = target.dataset.feedback;
    const discrepancyEl = target.closest('.discrepancy-card') as HTMLElement;
    const discrepancyType = discrepancyEl?.dataset.type || 'unknown';
    const severity = discrepancyEl?.dataset.severity || 'unknown';

    // Mark this button as selected
    const row = target.closest('.feedback-row');
    row?.querySelectorAll('.feedback-btn').forEach(btn => btn.classList.remove('selected'));
    target.classList.add('selected');

    // Track the feedback
    track('discrepancy_feedback', {
      discrepancy_id: discrepancyId,
      discrepancy_type: discrepancyType,
      severity: severity,
      feedback_type: feedback,
    });

    // Visual feedback
    target.textContent = feedback === 'helpful' ? '👍 Thanks!' : '👎 Thanks!';
  }
});

// JSZip is loaded via jsZipPromise at the top of the file
